import { useStoreState } from "../../../../packages/Footer/store/useStore";
import useColor from "../../../../packages/Header/hooks/useColor";
import { useEffect, useMemo, useState } from "react";
import useFooterAction from "../../../../packages/Footer/hooks/useFooterAction";

export interface FooterMainProps {
  label: string;
  menus: {
    title: string;
    link: string;
    outbound: boolean;
    isSupportSubAccount?: boolean;
    extraBadges?: string[];
  }[];
}

export interface FooterMenuProps {
  label: string;
  menus: {
    title: string;
    link: string;
    outbound: boolean;
    extraBadges?: string[];
    isSupportSubAccount?: boolean;
  }[];
}

const useFooter = () => {
  const [hasJourney, setHasJourney] = useState<boolean>(false);
  const { localeLink, baseUrl, env } = useStoreState();
  const { fetchJourneyUserInfo } = useFooterAction();

  useEffect(() => {
    if (baseUrl) {
      const initJourney = async () => {
        try {
          const res = await fetchJourneyUserInfo<{ code: number; data: any }>();
          if (res.code === 200) {
            setHasJourney(true);
          }
        } catch (error) {
          console.log("🚀 ~ useEffect ~ error:", error);
        }
      };
      initJourney();
    }
  }, [baseUrl]);

  const { isUk } = useColor(baseUrl, env);

  const FOOTER_MAIN: FooterMainProps[] = [
    {
      label: "about",
      menus: [
        { title: "aboutUs", link: "/about-us", outbound: false },
        {
          title: "terms",
          link: "https://support.blofin.com/hc/en-us/articles/7296238795279-TERM-OF-USE",
          outbound: true
        },
        { title: "privacy", link: "/privacy", outbound: true },
        {
          title: "riskWarning",
          link: "https://support.blofin.com/hc/en-us/articles/7298206673423-RISK-DISCLOSURE-STATEMENT",
          outbound: true
        },
        {
          title: "announcements",
          link: "/support/Announcement",
          outbound: false
        },
        { title: "bbbbbbApp", link: `/download`, outbound: false },
        {
          title: "wallet",
          link: "https://wallet.blofin.com/en",
          outbound: true,
          extraBadges: ["NEW"]
        }
      ]
    },
    {
      label: "products",
      menus: [
        { title: "buyCrypto", link: "/crypto?", outbound: false, isSupportSubAccount: false },
        { title: "convert", link: "/convert", outbound: false, isSupportSubAccount: false },
        { title: "futureTrading", link: `/futures${localeLink}/BTC-USDT`, outbound: true },
        { title: "spotTrading", link: `/spot${localeLink}/BTC-USDT`, outbound: true },
        {
          title: "futureGrid",
          link: `/futures${localeLink}/BTC-USDT?mode=tools`,
          outbound: true,
          isSupportSubAccount: false
        },
        {
          title: "tradingBots",
          link: `/futures${localeLink}/bots-aggregation`,
          outbound: true,
          isSupportSubAccount: false
        },
        { title: "copyTrading", link: `/copy-trade`, outbound: false, isSupportSubAccount: false },
        {
          title: "subAccountInUTA",
          link: `/account/sub-account`,
          outbound: false,
          isSupportSubAccount: false
        },
        { title: "earn", link: `/earn`, outbound: false, isSupportSubAccount: false },
        {
          title: "welcomeBonus",
          link: "/task-center",
          outbound: false,
          isSupportSubAccount: false
        },
        {
          title: "activityCenter",
          link: `/activity/`,
          outbound: false,
          isSupportSubAccount: false
        },
        { title: "academy", link: `/academy`, outbound: false },
        { title: "news", link: `/news`, outbound: false },
        { title: "mcp", link: `/blofin-mcp`, outbound: false },
        { title: "skillsHub", link: `/blofin-skills-hub`, outbound: false }
      ]
    },
    {
      label: "trade",
      menus: [
        { title: "btc-usdt", link: `/futures${localeLink}/BTC-USDT`, outbound: true },
        { title: "eth-usdt", link: `/futures${localeLink}/ETH-USDT`, outbound: true },
        { title: "doge-usdt", link: `/futures${localeLink}/DOGE-USDT`, outbound: true },
        { title: "sol-usdt", link: `/futures${localeLink}/SOL-USDT`, outbound: true },
        { title: "xrp-usdt", link: `/futures${localeLink}/XRP-USDT`, outbound: true },
        { title: "cryptoPrice", link: "/price/", outbound: false },
        { title: "bitcoinPrice", link: `/price/coins/bitcoin`, outbound: false },
        { title: "ethereumPrice", link: "/price/coins/ethereum", outbound: false },
        { title: "solanaPrice", link: "/price/coins/solana", outbound: false },
        { title: "xrpPrice", link: "/price/coins/ripple", outbound: false }
      ]
    }
  ];

  const FOOTER_MENU: FooterMenuProps[] = useMemo(() => {
    const serviceMenus = [
      { title: "affiliates", link: "/affiliates", outbound: false, isSupportSubAccount: false },
      ...(isUk
        ? []
        : [
            {
              title: "referral",
              link: "/referral?source=footer",
              outbound: false,
              isSupportSubAccount: false
            }
          ]),
      // { title: 'ambassador', link: '/ambassador', outbound: false },
      { title: "data", link: "/data/information/BTC-USDT", outbound: false },
      {
        title: "proofOfReserves",
        link: "/proof-of-reserves",
        outbound: false,
        isSupportSubAccount: false
      },
      {
        title: "listingApplication",
        link: "/coin-listing-apply",
        outbound: false,
        isSupportSubAccount: false
      },
      { title: "apis", link: "/apis", outbound: false },
      { title: "taxApi", link: "/account/tax-api", outbound: false, extraBadges: ["BETA"] },
      { title: "vip", link: "/vip", outbound: false, isSupportSubAccount: false }
    ];

    if (hasJourney) {
      serviceMenus.push({
        title: "journey",
        link: "/journey",
        outbound: false,
        isSupportSubAccount: false
      });
    }

    return [
      {
        label: "support",
        menus: [
          {
            title: "helpCenter",
            link: "/support",
            outbound: false
          },
          {
            title: "submitRequest",
            link: "/support/Submit-a-Request",
            outbound: false
          },
          // { title: 'apis', link: '/account/apis', outbound: false },
          { title: "fees", link: "/fees", outbound: false, isSupportSubAccount: false },
          {
            title: "officialVerification",
            link: "/official-verification",
            outbound: false,
            isSupportSubAccount: false
          }
        ]
      },
      {
        label: "service",
        menus: serviceMenus
      }
    ];
  }, [isUk, hasJourney]);

  const simpleUrl = [
    {
      title: "data",
      link: "/data/information/BTC-USDT",
      outbound: false
    },
    { title: "fees", link: "/fees", outbound: false },
    { title: "apis", link: "/apis", outbound: false, hot: false },
    {
      title: "helpCenter",
      link: "/support",
      outbound: false
    }
  ];

  return {
    FOOTER_MAIN,
    FOOTER_MENU,
    simpleUrl
  };
};

export default useFooter;
